import { SearchMenuStates } from 'components/Search/type'
import SearchFormFieldGroup from 'components/SearchV2/Components/SearchFormField/SearchFormFieldGroup'
import React, { useCallback, useContext, useMemo, useRef, useState } from 'react'
import { GlobalSearchDispatchContext } from 'contexts/GlobalSearch/GlobalSearchContexts'
import Group from 'components/utils/Group'
import { pluralizeToString } from 'lib/string/pluralize'
import LineAngleDownIcon from 'LuxKit/Icons/line/LineAngleDownIcon'
import LineUserIcon from 'LuxKit/Icons/line/LineUserIcon'
import AssistChip from 'LuxKit/Chips/AssistChip'
import TravellersDesktop from '../shared/travellers/Desktop'
import HiddenInput from 'components/Common/Form/Input/HiddenInput'
import { GlobalSearchStateActions } from 'contexts/GlobalSearch/GlobalSearchState'
import RoomsDesktop from '../shared/rooms/Desktop'
import { useMultiRoomSelectionSearchBar } from 'businessTraveller/hooks/useMultiRoomSelection'

interface Props {
  className?: string;
  activeMenu: SearchMenuStates;
  onMenuDismiss: () => void
  openEmployeeMenu: (e: React.MouseEvent<HTMLButtonElement>) => void
  openRoomMenu: (e: React.MouseEvent<HTMLButtonElement>) => void
  focusLocationInput: () => void
}

function BusinessTravellerSelectHotelsDesktop(props: Props) {
  const {
    className,
    activeMenu,
    openEmployeeMenu,
    openRoomMenu,
    focusLocationInput,
    onMenuDismiss,
  } = props

  const globalSearchDispatch = useContext(GlobalSearchDispatchContext)

  const {
    travellerEmployees,
    guestCount,
    totalTravellers,
    occupancies,
    roomCount,
    onAddEmployee,
    onRemoveEmployee,
    onAddGuest,
    onRemoveGuest,
    onRoomCountChange,
  } = useMultiRoomSelectionSearchBar()

  const localInputRef = useRef<HTMLButtonElement>(null)
  const roomInputRef = useRef<HTMLButtonElement>(null)
  const [error, setError] = useState<string>()

  const fieldValue = useMemo(() => {
    if (totalTravellers === 0) {
      return undefined
    }
    const primaryTraveller = travellerEmployees[0]
    if (!primaryTraveller) {
      return `${totalTravellers} guests`
    }
    if (totalTravellers === 1) {
      return `${primaryTraveller.firstName} ${primaryTraveller.lastName}`
    }
    return `${primaryTraveller.firstName} ${primaryTraveller.lastName} +${totalTravellers - 1} more`
  }, [travellerEmployees, totalTravellers])

  const setActiveMenu = useCallback((menu: SearchMenuStates) => {
    globalSearchDispatch({ type: GlobalSearchStateActions.SET_ACTIVE_MENU, menu })
  }, [globalSearchDispatch])

  const handleSelectTravellerApply = useCallback(() => {
    // Go to Rooms menu if there are multiple travellers
    if (totalTravellers > 1) {
      setActiveMenu(SearchMenuStates.BusinessTravellerMultiRoomsHotelSelect)
    } else {
      // Otherwise just close the menu
      onMenuDismiss()
    }
  }, [totalTravellers, setActiveMenu, onMenuDismiss])

  return (
    <SearchFormFieldGroup className={className}>
      <HiddenInput
        value={fieldValue}
        name="travellers"
        required
        invalidMessage="Please select traveller"
        onErrorUpdate={setError}
      />
      <Group direction="horizontal" gap={12}>
        <AssistChip
          variant="outline"
          size="medium"
          startIcon={<LineUserIcon />}
          endIcon={<LineAngleDownIcon />}
          onClick={openEmployeeMenu}
          ref={localInputRef}
          error={!!error}
        >
          {fieldValue && <>Travellers: <b>{fieldValue}</b></>}
          {!fieldValue && 'Add travellers'}
        </AssistChip>
        <AssistChip
          variant="outline"
          size="medium"
          endIcon={<LineAngleDownIcon />}
          onClick={openRoomMenu}
          ref={roomInputRef}
        >
          {pluralizeToString('room', roomCount)}
        </AssistChip>
      </Group>
      <TravellersDesktop
        open={activeMenu === SearchMenuStates.BusinessTravellerEmployeeSelect}
        onClose={onMenuDismiss}
        onApply={handleSelectTravellerApply}
        inputRef={localInputRef}
        totalTravellers={totalTravellers}
        onAddEmployee={onAddEmployee}
        onRemoveEmployee={onRemoveEmployee}
        onAddGuest={onAddGuest}
        onRemoveGuest={onRemoveGuest}
        travellerEmployees={travellerEmployees}
        guestCount={guestCount}
      />
      <RoomsDesktop
        open={activeMenu === SearchMenuStates.BusinessTravellerMultiRoomsHotelSelect}
        onClose={onMenuDismiss}
        onApply={focusLocationInput}
        roomInputRef={roomInputRef}
        rooms={occupancies}
        onRoomCountChange={onRoomCountChange}
        travellerCount={totalTravellers}
      />
    </SearchFormFieldGroup>
  )
}

export default BusinessTravellerSelectHotelsDesktop
